import "core-js/modules/es.array.includes.js";
import React, { useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getColoredBackgroundShade, pxToRem } from 'utils/ui';
import { BASIC } from 'styles/spacingPresets';
import { fillParent } from 'styles/mixins';
import Box from 'components/Box';
import Button from 'components/Button';
import Card from 'components/Card';
import { Checkbox } from 'components/CheckboxOption';
import Flex from 'components/Flex';
import Heading from 'components/Heading';
import Hidden from 'components/Hidden';
import DotIcon from 'components/icons/lib/Dot';
import InlineList from 'components/InlineList';
import Pill from 'components/Pill';
import Text from 'components/Text';
import VisuallyHidden from 'components/VisuallyHidden';
import ButtonCore from 'components/ButtonCore';
import DistributedContentIcon from 'components/DistributedContentIcon';
import { Image, ProcessingStatus, Ranking } from '../shared';
import useProcessingFileStatus from 'actions/file/useProcessingFileStatus';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var StyledCard = styled(Card).withConfig({
  displayName: "SelectableContentCard__StyledCard",
  componentId: "sc-10npiyf-0"
})(["position:relative;&::after{content:'';", " pointer-events:none;border:1px solid ", ";border-radius:inherit;opacity:0;transition:none;}&:hover::after,&[data-selected='true']::after{opacity:1;transition:opacity 0.1s linear 0.2s;}&[data-selected='true']::after{transition-delay:0s;box-shadow:", " 0 0 0 ", ";}&[data-selected='true'],&[data-disabled='true']{background-color:", ";}"], fillParent, function (p) {
  return p.theme.links;
}, pxToRem(-3), function (p) {
  return p.theme.links;
}, function (p) {
  return getColoredBackgroundShade(p.theme);
});

// This component provides an accessible focus outline
// for the visually hidden "toggle single select" button
export var SingleSelectButtonFocusWrapper = styled(Box).withConfig({
  displayName: "SelectableContentCard__SingleSelectButtonFocusWrapper",
  componentId: "sc-10npiyf-1"
})(["&:focus-within{outline:3px solid ", ";}"], function (p) {
  return p.theme.links;
});
export function useSelectableCardHandlers(_ref) {
  var toggleSelection = _ref.toggleSelection,
    onClick = _ref.onClick;
  var checkboxRef = useRef(null);
  var checkboxContainerRef = useRef(null);
  function handleCheckboxContainerClick(event) {
    var _checkboxRef$current;
    if (// Bail out if the checkbox was clicked…
    (_checkboxRef$current = checkboxRef.current) !== null && _checkboxRef$current !== void 0 && _checkboxRef$current.contains(event.target)) {
      return;
    }
    toggleSelection();
  }
  function handleCardClick(event) {
    var _checkboxContainerRef, _document$getSelectio, _checkboxRef$current2;
    if (
    // Bail out if the checkbox container was clicked…
    (_checkboxContainerRef = checkboxContainerRef.current) !== null && _checkboxContainerRef !== void 0 && _checkboxContainerRef.contains(event.target) ||
    // …or if selection was made
    ((_document$getSelectio = document.getSelection()) === null || _document$getSelectio === void 0 ? void 0 : _document$getSelectio.type) === 'Range') {
      return;
    }
    onClick();
    (_checkboxRef$current2 = checkboxRef.current) === null || _checkboxRef$current2 === void 0 || _checkboxRef$current2.focus();
  }
  return {
    checkboxRef: checkboxRef,
    checkboxContainerRef: checkboxContainerRef,
    handleCheckboxContainerClick: handleCheckboxContainerClick,
    handleCardClick: handleCardClick
  };
}
export function SelectableContentCard(_ref2) {
  var isSelected = _ref2.isSelected,
    isSelectionDisabled = _ref2.isSelectionDisabled,
    onToggleSelection = _ref2.onToggleSelection,
    onClick = _ref2.onClick,
    onMouseOver = _ref2.onMouseOver,
    onRequestEdit = _ref2.onRequestEdit,
    title = _ref2.title,
    translatedType = _ref2.translatedType,
    type = _ref2.type,
    changesCount = _ref2.changesCount,
    icon = _ref2.icon,
    imageSource = _ref2.imageSource,
    isOptional = _ref2.isOptional,
    number = _ref2.number,
    isDistributed = _ref2.isDistributed,
    fileId = _ref2.fileId,
    hasPlaylistNumbering = _ref2.hasPlaylistNumbering,
    contentFileStatus = _ref2.contentFileStatus,
    isContentEditable = _ref2.isContentEditable;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  function toggleSelection() {
    onToggleSelection(!isSelected);
  }
  var _useProcessingFileSta = useProcessingFileStatus({
      fileId: fileId,
      refetchInterval: 5000,
      // if the filestatus is processing, it will refetch every 5 seconds
      assetFileStatus: ['upload', 'web', 'zip', 'scorm'].includes(type) // check if it's an asset
      ? contentFileStatus : undefined
    }),
    fileStatus = _useProcessingFileSta.fileStatus,
    isProcessingStatusShowing = _useProcessingFileSta.isProcessingStatusShowing;
  var _useSelectableCardHan = useSelectableCardHandlers({
      onClick: onClick,
      toggleSelection: toggleSelection
    }),
    checkboxRef = _useSelectableCardHan.checkboxRef,
    checkboxContainerRef = _useSelectableCardHan.checkboxContainerRef,
    handleCheckboxContainerClick = _useSelectableCardHan.handleCheckboxContainerClick,
    handleCardClick = _useSelectableCardHan.handleCardClick;
  var titleId = "".concat(number, "-title");
  return /*#__PURE__*/_jsx(StyledCard, {
    forwardedAs: "article",
    "aria-labelledby": titleId,
    pr: "s",
    width: "auto",
    overflow: "visible",
    cursor: "pointer",
    "data-selected": isSelected && !isSelectionDisabled,
    "data-disabled": isSelectionDisabled,
    onClick: handleCardClick,
    onMouseOver: onMouseOver,
    children: /*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(Flex, {
        ref: checkboxContainerRef,
        onClick: handleCheckboxContainerClick,
        flexAlign: "stretch",
        align: "center",
        z: function z(theme) {
          return theme.globals.z.raised + 1;
        },
        px: BASIC.SMALL,
        breakpoints: BASIC.BREAKPOINTS,
        cursor: "default",
        children: /*#__PURE__*/_jsx(Checkbox, {
          ref: checkboxRef,
          checked: isSelected,
          onChange: toggleSelection,
          disabled: isSelectionDisabled,
          size: ['small', 'default'],
          breakpoints: "s",
          "aria-labelledby": titleId
        })
      }), /*#__PURE__*/_jsx(Flex, {
        backgroundColor: "shaded",
        flexAlign: "stretch",
        width: "15%",
        minWidth: 36,
        maxWidth: [75, 112],
        breakpoints: "s",
        mr: "s",
        children: /*#__PURE__*/_jsx(Image, {
          icon: icon,
          iconScale: 1,
          colorBlock: icon,
          aspectRatio: 2 / 3,
          maxHeight: 77,
          src: imageSource
        })
      }), /*#__PURE__*/_jsxs(Box, {
        grow: true,
        shrink: true,
        py: "xs",
        children: [/*#__PURE__*/_jsxs(SingleSelectButtonFocusWrapper, {
          wrapItems: true,
          shrink: true,
          display: "flex",
          alignItems: "baseline",
          columnGap: "xs",
          rowGap: "xxxs",
          children: [/*#__PURE__*/_jsxs(Heading, {
            as: "h4",
            size: "small",
            display: "inline",
            children: [hasPlaylistNumbering && /*#__PURE__*/_jsx(Ranking, {
              rank: number
            }), /*#__PURE__*/_jsx(Text, {
              id: titleId,
              children: title
            })]
          }), isOptional && /*#__PURE__*/_jsx(Pill, {
            children: t('content_status_optional')
          }), isDistributed && /*#__PURE__*/_jsx(Pill, {
            children: /*#__PURE__*/_jsx(DistributedContentIcon, {
              type: type,
              vAlign: true
            })
          }), /*#__PURE__*/_jsx(VisuallyHidden, {
            children: /*#__PURE__*/_jsxs(ButtonCore, {
              type: "button",
              onClick: onClick,
              isActive: isSelected,
              children: [t('actions_toggle_single_select'), " ", title]
            })
          })]
        }), /*#__PURE__*/_jsx(Box, {
          fontSize: BASIC.SMALL,
          breakpoints: BASIC.BREAKPOINTS,
          mt: "xxxs",
          children: /*#__PURE__*/_jsxs(InlineList, {
            splitBy: "dot",
            children: [translatedType, isProcessingStatusShowing && fileStatus && /*#__PURE__*/_jsx(ProcessingStatus, {
              status: fileStatus
            }), changesCount > 0 && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(DotIcon, {
                vAlign: true,
                color: "highlight"
              }), /*#__PURE__*/_jsx(Hidden, {
                below: "xs",
                as: "span",
                accessible: true,
                ml: "xxs",
                children: t('state_number_of_unsaved_changes', {
                  count: changesCount
                })
              })]
            })]
          })
        })]
      }), isContentEditable && /*#__PURE__*/_jsx(Hidden, {
        below: "xxs",
        shrink: 0,
        children: /*#__PURE__*/_jsxs(Button, {
          variant: "transparent",
          size: "medium",
          onClick: function onClick(e) {
            // Stop this click event from triggering
            // the outer Card's `onClick` prop
            e.stopPropagation();
            onRequestEdit();
          },
          children: [t('actions_edit'), /*#__PURE__*/_jsxs(VisuallyHidden, {
            children: [" ", title]
          })]
        })
      })]
    })
  });
}